import type { ReactNode } from 'react'

import s from './CalendarDoor.module.css'

import { AppLink } from './AppLink'

export interface CalendarDoorProps {
  /**
   * Every listing there is — the size of London, not of the selection above. Null on a venue
   * page, which is a room rather than a picking and has nothing to be the whole of, so the
   * sentence there is the generic one.
   */
  events: number | null
}

/**
 * The panel at the foot of the front page that leads to the calendar.
 *
 * Its one line has to draw the distinction this whole site rests on: the page above is a few
 * dozen gigs somebody chose, and the calendar is every listing there is. A reader who does
 * not know that reads the front page as the whole thing and never opens the rest.
 *
 * The count goes inside the sentence rather than above it as a 96px figure, because a number
 * on its own says how big the pile is and this one has a job: it is the difference between
 * the two pages, and the reason the picking was worth doing.
 */
export function CalendarDoor({ events }: CalendarDoorProps) {
  return (
    <AppLink className={s.door} to="/calendar">
      <span className={s.say}>
        <span className={s.head}>The whole calendar</span>
        <span className={s.sub}>{sentence(events)}</span>
      </span>
      <span className={s.go}>
        Open the calendar
        <span className={s.arrow} aria-hidden="true">
          →
        </span>
      </span>
      {/* Decoration: the rule that fills on hover. It says nothing a reader needs to hear. */}
      <span className={s.fill} aria-hidden="true" />
    </AppLink>
  )
}

function sentence(events: number | null): ReactNode {
  if (events === null) {
    return 'Every night in London in order, by week or by month, and a page for each room.'
  }
  return (
    <>
      The page above is what got picked. The calendar is all{' '}
      {/* Pinned to en-GB rather than the reader's locale: the Python formatted this with a
          comma and the figure is set beside English words either side of it. */}
      <span className={s.fig}>{events.toLocaleString('en-GB')}</span> listings — every night in
      order, by week or by month, and a page for each room.
    </>
  )
}
